import SignOutButton from "@/components/shared/SignOutButton";
import { useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import {
  Heart, Plus, ChevronDown, CircleUser, HelpCircle, Menu, X,
} from "lucide-react";
import { formatRoom } from "@/lib/classroom";

/**
 * Layout for /parents/* pages — sidebar + mobile bar + main content area.
 * Mirrors SchoolLayout but for the parent experience.
 *
 * Props:
 *   school_name      — displayed in the sidebar header
 *   siblings         — [{ id, name, classroom, path, current }]
 *   claim_another_path — path to the "add another child" flow
 *   user_contexts    — [{ label, path, role, current }] for multi-context switching
 *   title            — page heading (inside <main>)
 *   eyebrow          — uppercase label above the title
 *   actions          — right-aligned header buttons (JSX)
 *   mobileTitle      — text shown in the mobile top bar (defaults to school_name)
 *   activeClaim      — true when the "Add another child" nav item is active
 *   children         — page content
 */
export default function ParentLayout({
  school_name,
  siblings,
  claim_another_path,
  user_contexts,
  title,
  eyebrow,
  actions,
  mobileTitle,
  activeClaim = false,
  children,
}) {
  const { auth } = usePage().props;
  const hasMultipleContexts = (user_contexts || []).length > 1;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const sidebarHeader = (
    <div className="p-5 border-b border-border">
      {hasMultipleContexts ? (
        <ContextSwitcher
          label={school_name || "My children"}
          contexts={user_contexts}
        />
      ) : (
        <div className="inline-flex items-center gap-2 font-display text-lg font-semibold text-foreground">
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-primary/10 text-primary shrink-0">
            <Heart className="h-4 w-4" />
          </span>
          <span className="min-w-0 break-words leading-tight line-clamp-2" title={school_name}>{school_name || "My children"}</span>
        </div>
      )}
    </div>
  );

  const sidebarNav = (
    <nav className="p-3 space-y-1">
      {(siblings || []).length > 0 ? (
        (siblings || []).map((s) => (
          <Link
            key={s.id}
            href={s.path}
            onClick={() => setMobileNavOpen(false)}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              s.current
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
            }`}
          >
            <Heart className="h-4 w-4" />
            <div className="min-w-0">
              <div className="truncate">{s.name}</div>
              {s.classroom && (
                <div className="text-[10px] text-muted-foreground">{formatRoom(s.classroom)}</div>
              )}
            </div>
          </Link>
        ))
      ) : (
        <div className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium bg-primary/10 text-primary">
          <Heart className="h-4 w-4" /> My children
        </div>
      )}
      {claim_another_path && (
        <Link
          href={claim_another_path}
          onClick={() => setMobileNavOpen(false)}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            activeClaim
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
          }`}
        >
          <Plus className="h-4 w-4" /> Add another child
        </Link>
      )}
      <Link
        href="/help/parents"
        onClick={() => setMobileNavOpen(false)}
        className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
      >
        <HelpCircle className="h-4 w-4" /> Help
      </Link>
    </nav>
  );

  const sidebarFooter = (
    <div className="p-3 mt-auto border-t border-border space-y-2">
      {auth?.user && (
        <div className="flex items-center gap-2.5 px-2">
          <CircleUser className="h-8 w-8 text-muted-foreground/50 shrink-0" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate" title={auth.user.email}>
              {auth.user.email}
            </p>
            <Link href="/preferences" className="text-xs text-muted-foreground underline-offset-2 hover:text-primary hover:underline">
              Email preferences
            </Link>
          </div>
          <SignOutButton />
        </div>
      )}
      <a href="/" className="block text-center text-[10px] text-muted-foreground/60 hover:text-primary transition-colors py-1">
        Powered by <span className="font-display font-semibold">PagePledge</span>
      </a>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-card focus:px-4 focus:py-2 focus:shadow-lg focus:ring-2 focus:ring-ring">
        Skip to main content
      </a>

      {/* ── Desktop sidebar ──────────────────────────── */}
      <aside className="hidden md:flex md:flex-col md:w-64 md:sticky md:top-0 md:h-screen md:overflow-y-auto border-r border-border bg-card/60 shrink-0">
        {sidebarHeader}
        {sidebarNav}
        {sidebarFooter}
      </aside>

      {/* ── Mobile top bar ────────────────────────────── */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border bg-card/60">
        <span className="font-display text-base font-semibold text-foreground truncate">
          {mobileTitle || school_name}
        </span>
        <button
          type="button"
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors shrink-0"
          aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileNavOpen}
        >
          {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* ── Mobile dropdown nav ───────────────────────── */}
      {mobileNavOpen && (
        <div className="md:hidden border-b border-border bg-card/60">
          {sidebarHeader}
          {sidebarNav}
          {sidebarFooter}
        </div>
      )}

      {/* ── Mobile child switcher (two or more children) ── */}
      {!mobileNavOpen && (siblings || []).length > 1 && (
        <div className="md:hidden flex gap-2 overflow-x-auto border-b border-border bg-card/40 px-4 py-2">
          {siblings.map((s) => (
            <Link
              key={s.id}
              href={s.path}
              aria-current={s.current ? "page" : undefined}
              className={`inline-flex min-h-9 shrink-0 items-center rounded-full border px-3 text-sm font-medium transition-colors ${
                s.current ? "border-primary/30 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {s.name}
            </Link>
          ))}
        </div>
      )}

      {/* ── Main content ──────────────────────────────── */}
      <main id="main" className="flex-1 min-w-0">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
          {(title || actions) && (
            <header className="mb-10 flex flex-wrap items-start justify-between gap-4">
              <div>
                {eyebrow && (
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    {eyebrow}
                  </p>
                )}
                {title && (
                  <h1 className="font-display text-3xl md:text-4xl font-semibold text-foreground">
                    {title}
                  </h1>
                )}
              </div>
              {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
            </header>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Internal sub-components
// ─────────────────────────────────────────────────────────────

function ContextSwitcher({ label, contexts }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 font-display text-lg font-semibold text-foreground hover:text-primary transition-colors text-left"
      >
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-primary/10 text-primary shrink-0">
          <Heart className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1 break-words leading-tight line-clamp-2" title={label}>{label}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-border bg-card shadow-lg overflow-hidden z-50">
          {contexts.map((ctx, i) => (
            <a
              key={i}
              href={ctx.path}
              className={`flex items-center justify-between px-4 py-2.5 text-sm hover:bg-secondary/60 transition-colors ${
                ctx.current ? "bg-primary/5 font-medium" : ""
              }`}
            >
              <span className="truncate">{ctx.label}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-2 shrink-0">{ctx.role}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

